import { type IconProps, Stroke } from "./base";

/** What a control does: search, close, add, edit, move a file, send. */

export function SearchIcon({ strokeWidth = 1.8, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <circle cx="11" cy="11" r="6.5" />
            <path d="M20 20l-4.3-4.3" />
        </Stroke>
    );
}

export function CloseIcon({ strokeWidth = 2, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M6 6l12 12M18 6L6 18" />
        </Stroke>
    );
}

export function PlusIcon({ strokeWidth = 2.6, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M12 5v14M5 12h14" />
        </Stroke>
    );
}

export function EditIcon({ strokeWidth = 2.2, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17z" />
        </Stroke>
    );
}

/** Upload a resume: an arrow up off a line. */
export function UploadIcon({ strokeWidth = 2.2, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M12 16V5M7 10l5-5 5 5M5 20h14" />
        </Stroke>
    );
}

/** A file going up: the drop zone's tile. */
export function FileUploadIcon({ strokeWidth = 2, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M7 3h7l5 5v13H7zM14 3v5h5" />
            <path d="M12 18v-6M9.5 14.5 12 12l2.5 2.5" />
        </Stroke>
    );
}

export function DownloadIcon({ strokeWidth = 2, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
        </Stroke>
    );
}

/** Paste the text: a type T. */
export function TextIcon({ strokeWidth = 1.7, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M8 4h8M12 4v16M9 20h6" />
        </Stroke>
    );
}

export function PaperPlaneIcon({ strokeWidth = 1.8, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="m22 2-7 20-4-9-9-4 20-7ZM22 2 11 13" />
        </Stroke>
    );
}

export function FastForwardIcon({ strokeWidth = 1.8, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="m3 5 9 7-9 7V5Zm9 0 9 7-9 7V5Z" />
        </Stroke>
    );
}

/** Saved: the bookmark, outlined, or filled once the posting is kept. */
export function BookmarkIcon({
    strokeWidth = 2,
    filled = false,
    ...props
}: IconProps & { filled?: boolean }) {
    return (
        <Stroke strokeWidth={strokeWidth} fill={filled ? "currentColor" : "none"} {...props}>
            <path d="M6 3h12v18l-6-4.5L6 21z" />
        </Stroke>
    );
}

/** New matches by email. */
export function BellIcon({ strokeWidth = 2, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </Stroke>
    );
}

export function SunIcon({ strokeWidth = 1.8, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </Stroke>
    );
}

export function MoonIcon({ strokeWidth = 1.8, ...props }: IconProps) {
    return (
        <Stroke strokeWidth={strokeWidth} {...props}>
            <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
        </Stroke>
    );
}

/** Three dots: more of what can be done here. */
export function MoreIcon({ className = "size-4", ...props }: IconProps) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className={className}
            {...props}
        >
            <circle cx="5" cy="12" r="1.8" />
            <circle cx="12" cy="12" r="1.8" />
            <circle cx="19" cy="12" r="1.8" />
        </svg>
    );
}

/** The six dots of a row that can be dragged. */
export function GripIcon({ className = "size-3", ...props }: IconProps) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className={className}
            {...props}
        >
            <circle cx="9" cy="6" r="1.6" />
            <circle cx="15" cy="6" r="1.6" />
            <circle cx="9" cy="12" r="1.6" />
            <circle cx="15" cy="12" r="1.6" />
            <circle cx="9" cy="18" r="1.6" />
            <circle cx="15" cy="18" r="1.6" />
        </svg>
    );
}
